/**
 * Sprint 4 — Migration des clients annulés (Zen Planner → Kaizen System).
 * Import TRAÇABLE (par lot) · VALIDÉ (dry-run + dédup) · RÉVERSIBLE (rollback).
 * Garde-fou : les clients importés N'entrent JAMAIS en facturation/rappel automatique.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Download, Loader2, RefreshCw, PlayCircle, CheckCircle2, Undo2, Users,
  Sparkles, DollarSign, Archive, Layers, Search, FlaskConical, ShieldCheck,
} from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";

const Kpi = ({ label, value, icon: Icon, accent, testid }) => (
  <div className={`rounded-2xl border p-4 ${accent}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wide text-zinc-400">{label}</span>
      <Icon className="w-4 h-4 opacity-70" />
    </div>
    <div className="mt-2 text-2xl font-bold">{value}</div>
  </div>
);

export default function CancelledMigrationModule() {
  const [tab, setTab] = useState("preview");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState({ reactivation: false, with_balance: false, q: "" });

  const loadCore = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/migration/cancelled/preview"),
        api.get("/migration/cancelled/stats"),
      ]);
      setPreview(p.data); setStats(s.data);
    } catch (e) { toast.error("Erreur de chargement de la migration"); }
    setLoading(false);
  };
  useEffect(() => { loadCore(); }, []);

  const loadTab = async (t) => {
    setTab(t);
    try {
      if (t === "batches") setBatches((await api.get("/migration/cancelled/batches")).data.batches);
      if (t === "clients") loadClients();
    } catch (e) { toast.error("Erreur de chargement"); }
  };

  const loadClients = async () => {
    const params = new URLSearchParams();
    if (filter.reactivation) params.set("reactivation", "true");
    if (filter.with_balance) params.set("with_balance", "true");
    if (filter.q) params.set("q", filter.q);
    const r = await api.get(`/migration/cancelled/clients?${params.toString()}`);
    setClients(r.data.clients);
  };

  const runImport = async (dryRun) => {
    if (!dryRun && !window.confirm(`Importer définitivement ${preview?.counts?.new || 0} client(s) annulé(s) ? (Réversible via rollback)`)) return;
    setBusy(true);
    try {
      const r = await api.post("/migration/cancelled/run", { dry_run: dryRun });
      const s = r.data.summary;
      if (dryRun) toast.success(`Simulation OK : ${s.counts.to_create} client(s) seraient créés`);
      else { toast.success(`Import réussi : ${s.counts.to_create} client(s) importés (lot ${s.batch_id.slice(0, 8)})`); }
      loadCore();
      if (tab === "batches") setBatches((await api.get("/migration/cancelled/batches")).data.batches);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action réservée aux admins"); }
    setBusy(false);
  };

  const rollback = async (batchId) => {
    if (!window.confirm("Annuler ce lot et SUPPRIMER tous ses clients importés ?")) return;
    try {
      const r = await api.post(`/migration/cancelled/batches/${batchId}/rollback`);
      toast.success(`Lot annulé : ${r.data.deleted} client(s) supprimés`);
      setBatches((await api.get("/migration/cancelled/batches")).data.batches);
      loadCore();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const toggleReactivation = async (id, val) => {
    await api.post(`/migration/cancelled/clients/${id}/update`, { reactivation_flag: val });
    toast.success(val ? "Marqué candidat réactivation" : "Retiré des candidats");
    loadClients();
  };

  const runCampaign = async (dryRun) => {
    if (!dryRun && !window.confirm("Envoyer le courriel de réactivation (en lot, garde-fous CASL) aux candidats avec courriel ?")) return;
    setBusy(true);
    try {
      const r = await api.post("/migration/cancelled/reactivation-campaign", { dry_run: dryRun, limit: 200 });
      const s = r.data.summary;
      if (s.disabled) toast.warning("Kill-switch recouvrement actif (envois désactivés)");
      else if (s.no_gmail) toast.warning("Gmail non connecté — reconnecte ton compte dans Intégrations");
      else if (dryRun) toast.success(`Simulation : ${s.sent} courriel(s) seraient envoyés à ${s.eligible} candidat(s)`);
      else toast.success(`Campagne envoyée : ${s.sent} courriel(s), ${s.followups_created} suivi(s) créés`);
      loadCore();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action réservée aux admins"); }
    setBusy(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  const c = preview?.counts || {};

  return (
    <div className="space-y-6 text-zinc-100 p-6 md:p-8" data-testid="cancelled-migration-module">
      <PageHeader
        title="Migration des clients annulés"
        icon={Download}
        breadcrumbs={[{ label: "CRM & relations" }, { label: "Migration clients annulés" }]}
        subtitle={<>Import traçable, validé et <span className="text-emerald-300">réversible</span>. Aucune facturation/rappel auto.</>}
        actions={
          <button onClick={loadCore} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm flex items-center gap-1" data-testid="migration-refresh"><RefreshCw className="w-4 h-4" /> Actualiser</button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Source (annulés)" value={c.total_source || 0} icon={Users} accent="border-zinc-700 bg-zinc-900" testid="kpi-source" />
        <Kpi label="Déjà importés" value={stats?.total_imported || 0} icon={CheckCircle2} accent="border-emerald-500/30 bg-emerald-500/5" testid="kpi-imported" />
        <Kpi label="Candidats réactivation" value={stats?.reactivation_candidates || 0} icon={Sparkles} accent="border-blue-500/30 bg-blue-500/5" testid="kpi-reactivation" />
        <Kpi label="Avec solde" value={stats?.with_balance || 0} icon={DollarSign} accent="border-amber-500/30 bg-amber-500/5" testid="kpi-balance" />
        <Kpi label="Archivés (sans contact)" value={stats?.archived || 0} icon={Archive} accent="border-zinc-700 bg-zinc-900" testid="kpi-archived" />
        <Kpi label="Lots d'import" value={stats?.batches || 0} icon={Layers} accent="border-zinc-700 bg-zinc-900" testid="kpi-batches" />
      </div>

      <div className="flex gap-2 border-b border-zinc-800 flex-wrap">
        {[["preview", "Aperçu & Import", FlaskConical], ["batches", "Lots", Layers], ["clients", "Clients importés", Users]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => loadTab(id)} data-testid={`migration-tab-${id}`}
            className={`px-3 py-2 text-sm flex items-center gap-1 border-b-2 -mb-px ${tab === id ? "border-amber-400 text-amber-300" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Aperçu & Import */}
      {tab === "preview" && (
        <div className="space-y-4" data-testid="migration-preview">
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
            <div className="text-sm font-medium mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Analyse de dédoublonnage (source : statut « {(preview?.source_statuses || []).join(", ")} »)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="rounded-lg bg-zinc-800/50 p-3"><div className="text-xs text-zinc-400">Nouveaux à importer</div><div className="text-xl font-bold text-emerald-300">{c.new || 0}</div></div>
              <div className="rounded-lg bg-zinc-800/50 p-3"><div className="text-xs text-zinc-400">Doublons (membres actifs)</div><div className="text-xl font-bold text-amber-300">{c.duplicate_active || 0}</div></div>
              <div className="rounded-lg bg-zinc-800/50 p-3"><div className="text-xs text-zinc-400">Déjà migrés</div><div className="text-xl font-bold text-blue-300">{c.already_migrated || 0}</div></div>
              <div className="rounded-lg bg-zinc-800/50 p-3"><div className="text-xs text-zinc-400">Sans contact (archive)</div><div className="text-xl font-bold text-zinc-300">{c.no_contact || 0}</div></div>
            </div>
            <div className="flex gap-2 mt-4 flex-wrap">
              <button onClick={() => runImport(true)} disabled={busy} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="migration-dryrun">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} Simuler (dry-run)
              </button>
              <button onClick={() => runImport(false)} disabled={busy || !c.new} className="px-4 py-2 rounded-lg bg-amber-600/30 hover:bg-amber-600/50 text-amber-100 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="migration-import">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Importer {c.new || 0} client(s)
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase"><tr><th className="text-left p-3">Nom</th><th className="text-left p-3">Courriel</th><th className="text-left p-3">Téléphone</th><th className="text-left p-3">Statut source</th><th className="text-left p-3">Marqueurs</th></tr></thead>
              <tbody>
                {(preview?.rows || []).map((r, i) => (
                  <tr key={i} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                    <td className="p-3 font-medium text-amber-100">{r.full_name || "—"}</td>
                    <td className="p-3 text-xs text-zinc-400">{r.email || "—"}</td>
                    <td className="p-3 text-xs text-zinc-400">{r.phone || "—"}</td>
                    <td className="p-3 text-xs">{r.zp_status}</td>
                    <td className="p-3 text-xs space-x-1">
                      {r.reactivation && <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">réactivation</span>}
                      {r.has_balance && <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">solde</span>}
                      {!r.contactable && <span className="px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300">archive</span>}
                    </td>
                  </tr>
                ))}
                {(!preview?.rows || preview.rows.length === 0) && <tr><td colSpan={5} className="p-8 text-center text-zinc-500">Aucun nouveau client annulé à importer.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lots */}
      {tab === "batches" && (
        <div className="space-y-2" data-testid="migration-batches-list">
          {batches.length === 0 && <p className="text-zinc-500 text-sm p-4">Aucun lot d'import pour l'instant.</p>}
          {batches.map((b) => (
            <div key={b.batch_id} className="rounded-xl border border-zinc-800 bg-zinc-900 p-3 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-medium flex items-center gap-2">Lot {b.batch_id.slice(0, 8)}
                  <span className={`px-2 py-0.5 rounded-full text-[11px] ${b.status === "completed" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-600/30 text-rose-200"}`}>{b.status}</span>
                </div>
                <div className="text-xs text-zinc-400 mt-1">{b.counts?.to_create || 0} client(s) · par {b.operator} · {new Date(b.created_at).toLocaleString("fr-CA")}</div>
              </div>
              {b.status === "completed" && (
                <button onClick={() => rollback(b.batch_id)} className="px-3 py-1.5 rounded bg-rose-600/30 hover:bg-rose-600/50 text-xs flex items-center gap-1" data-testid={`rollback-${b.batch_id}`}><Undo2 className="w-3 h-3" /> Annuler (rollback)</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Clients importés */}
      {tab === "clients" && (
        <div className="space-y-3" data-testid="migration-clients">
          {(stats?.reactivation_candidates || 0) > 0 && (
            <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="reactivation-campaign-box">
              <div>
                <div className="text-sm font-medium flex items-center gap-2"><Sparkles className="w-4 h-4 text-blue-400" /> Campagne de réactivation</div>
                <div className="text-xs text-zinc-400 mt-1">{stats.reactivation_candidates} candidat(s) avec courriel. Envoi en lot contrôlé (garde-fous CASL) + crée un suivi par client contacté.</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => runCampaign(true)} disabled={busy} className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs flex items-center gap-1 disabled:opacity-50" data-testid="campaign-dryrun"><FlaskConical className="w-3 h-3" /> Simuler</button>
                <button onClick={() => runCampaign(false)} disabled={busy} className="px-3 py-1.5 rounded-lg bg-blue-600/30 hover:bg-blue-600/50 text-blue-100 text-xs flex items-center gap-1 disabled:opacity-50" data-testid="campaign-send"><PlayCircle className="w-3 h-3" /> Lancer la campagne</button>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-zinc-500" />
              <input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && loadClients()}
                placeholder="Rechercher nom/courriel…" className="bg-zinc-900 border border-zinc-700 rounded pl-8 pr-3 py-1.5 text-sm" data-testid="migration-search" />
            </div>
            <label className="text-xs flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={filter.reactivation} onChange={(e) => setFilter({ ...filter, reactivation: e.target.checked })} /> Réactivation</label>
            <label className="text-xs flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={filter.with_balance} onChange={(e) => setFilter({ ...filter, with_balance: e.target.checked })} /> Avec solde</label>
            <button onClick={loadClients} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-xs" data-testid="migration-apply-filter">Filtrer</button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase"><tr><th className="text-left p-3">Nom</th><th className="text-left p-3">Courriel</th><th className="text-left p-3">Marqueurs</th><th className="text-right p-3">Réactivation</th></tr></thead>
              <tbody>
                {clients.map((cl) => (
                  <tr key={cl.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                    <td className="p-3 font-medium text-amber-100">{cl.full_name || "—"}</td>
                    <td className="p-3 text-xs text-zinc-400">{cl.email || "—"}</td>
                    <td className="p-3 text-xs space-x-1">{(cl.status_tags || []).map((t) => <span key={t} className="px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300">{t.replace("imported_", "")}</span>)}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => toggleReactivation(cl.id, !cl.reactivation_flag)} className={`px-2 py-1 rounded text-xs flex items-center gap-1 ml-auto ${cl.reactivation_flag ? "bg-blue-600/40 text-blue-100" : "bg-zinc-800 text-zinc-400"}`} data-testid={`reactivation-${cl.id}`}><Sparkles className="w-3 h-3" /> {cl.reactivation_flag ? "Candidat" : "Non"}</button>
                    </td>
                  </tr>
                ))}
                {clients.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-zinc-500">Aucun client importé (lance un import dans l'onglet Aperçu).</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
